import React from 'react';

import { LoadableElement } from '@99designs/common/components/LoadableElement';
import {
    getSquareOnlineStoreBrief,
    getSquareOnlineStoreBriefVariables,
} from '__generated__/getSquareOnlineStoreBrief';
import { SquareOnlineStoreBriefFields } from '__generated__/SquareOnlineStoreBriefFields';
import {
    updateSquareOnlineStoreBrief,
    updateSquareOnlineStoreBriefVariables,
} from '__generated__/updateSquareOnlineStoreBrief';
import { handleGraphQLError } from 'common/utils/Apollo';
import { AuthConsumer } from 'projects/components/AuthContext';
import DesignerMetaContext, { DesignerMetaLayouts } from 'projects/components/DesignerMetaContext';
import { errorNotFound, errorServerError } from 'projects/components/ErrorBoundary';
import { routeForBrief } from 'projects/routes';
import Brief from 'projects/routes/Brief';
import InviteDesignersPage from 'projects/routes/InviteDesignersPage';
import AboutYouPage from 'projects/routes/squareOnlineStore/AboutYouPage';
import DetailsPage from 'projects/routes/squareOnlineStore/DetailsPage';
import SummaryPage from 'projects/routes/squareOnlineStore/SummaryPage';
import TimeAndBudgetPage from 'projects/routes/squareOnlineStore/TimeAndBudgetPage';
import { Industries } from 'projects/util/industries';
import { createStepState, StepConfiguration } from 'projects/util/step';
import { ApolloConsumer, Query } from 'react-apollo';
import { Redirect, RouteComponentProps, withRouter } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import SquareAuthModal from './SquareAuthModal';
import SquareAuthModalDirect from './SquareAuthModalDirect';
import queries from './SquareOnlineStoreBrief.graphql';
import WelcomeOverlay from './WelcomeOverlay';

type Props = RouteComponentProps<{
    briefId: string;
    step: string;
}>;

export const SquareOnlineStoreBrief: React.FunctionComponent<Props> = props => (
    <Query<getSquareOnlineStoreBrief, getSquareOnlineStoreBriefVariables>
        query={queries.getSquareOnlineStoreBrief}
        variables={{ id: props.match.params.briefId }}
    >
        {({ error, loading, data }) => {
            if (loading) {
                return <LoadableElement variations={['fade-in']} isLoading={true} />;
            }

            if (error) {
                handleGraphQLError(error);
            }

            if (!data) {
                throw errorServerError;
            }

            if (!data.brief) {
                throw errorNotFound;
            }

            if (data.brief.fields.__typename !== 'SquareOnlineStoreBriefFields') {
                return (
                    <Redirect
                        to={routeForBrief(
                            data.brief.fields.__typename,
                            data.brief.id,
                            props.location.search
                        )}
                    />
                );
            }

            const steps: StepConfiguration[] = [
                {
                    step: 1,
                    title: __('Tell us about your project'),
                },
                { step: 2, title: __('Your time & budget') },
                { step: 3, title: __('About you') },
                { step: 4, title: __('Review your brief') },
                {
                    step: 5,
                    title: __('Invite designers to quote'),
                    requiresAuth: true,
                    isDesignerStep: true,
                    nextActionLabel: __('Confirm designers'),
                },
            ];

            const step = createStepState({
                steps,
                brief: data.brief,
                currentStep: parseInt(props.match.params.step, 10),
                designerSources: data.brief.designersWithSource.map(d => d.source),
            });

            const brief = {
                ...data.brief,
                fields: data.brief.fields as SquareOnlineStoreBriefFields,
            };

            const industries = new Industries(data.industries);

            return (
                <ApolloConsumer>
                    {client => (
                        <AuthConsumer>
                            {({ login }) => (
                                <Brief
                                    brief={brief}
                                    viewer={data.viewer}
                                    login={login}
                                    step={step}
                                    client={client}
                                    recommendedAuthModal={SquareAuthModal}
                                    directAuthModal={SquareAuthModalDirect}
                                    onSave={async changes => {
                                        const res = await client.mutate<
                                            updateSquareOnlineStoreBrief,
                                            updateSquareOnlineStoreBriefVariables
                                        >({
                                            mutation: queries.updateSquareOnlineStoreBrief,
                                            variables: {
                                                id: brief.id,
                                                fields: { squareOnlineStore: changes },
                                            },
                                        });
                                        return res ? res.data!.brief.errors : [];
                                    }}
                                >
                                    {form => (
                                        <Switch>
                                            <Route
                                                path={`/briefs/square-online-store/:briefId/1`}
                                                render={() => (
                                                    <div>
                                                        <WelcomeOverlay brief={brief} />
                                                        <DetailsPage form={form} brief={brief} />
                                                    </div>
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/square-online-store/:briefId/2`}
                                                render={() => (
                                                    <TimeAndBudgetPage
                                                        form={form}
                                                        brief={brief}
                                                        site={data.site}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/square-online-store/:briefId/3`}
                                                render={() => (
                                                    <AboutYouPage
                                                        form={form}
                                                        industries={data.industries}
                                                        brief={brief}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/square-online-store/:briefId/4`}
                                                render={() => (
                                                    <SummaryPage
                                                        brief={brief}
                                                        industries={industries}
                                                    />
                                                )}
                                            />
                                            <Route
                                                path={`/briefs/square-online-store/:briefId/5`}
                                                render={() => (
                                                    <DesignerMetaContext.Provider
                                                        value={{
                                                            layout: DesignerMetaLayouts.compact,
                                                            hasMoreResultsUrl: false,
                                                            hasResponsiveness: false,
                                                        }}
                                                    >
                                                        <InviteDesignersPage
                                                            brief={brief}
                                                            viewer={data.viewer}
                                                        >
                                                            {__(
                                                                'Here are the Square Online Store experts that best match your needs'
                                                            )}
                                                        </InviteDesignersPage>
                                                    </DesignerMetaContext.Provider>
                                                )}
                                            />
                                        </Switch>
                                    )}
                                </Brief>
                            )}
                        </AuthConsumer>
                    )}
                </ApolloConsumer>
            );
        }}
    </Query>
);

export default withRouter(SquareOnlineStoreBrief);
